"use client"

import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from "lucide-react"
import { useTheme } from "next-themes"
import { Toaster as Sonner, type ToasterProps } from "sonner"

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  return (
    <>
      <Sonner
        theme={theme as ToasterProps["theme"]}
        className="toaster group"
        icons={{
          success: <CircleCheckIcon className="size-4" />,
          info: <InfoIcon className="size-4" />,
          warning: <TriangleAlertIcon className="size-4" />,
          error: <OctagonXIcon className="size-4" />,
          loading: <Loader2Icon className="size-4 animate-spin" />,
        }}
        toastOptions={{
          unstyled: true,
          classNames: {
            toast: 'noir-toast',
            title: 'noir-toast-title',
            description: 'noir-toast-desc',
            icon: 'noir-toast-icon',
            success: 'noir-toast--success',
            warning: 'noir-toast--warning',
            error: 'noir-toast--error',
            info: 'noir-toast--info',
          },
        }}
        {...props}
      />
      <style>{`
        .noir-toast {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 18px;
          width: 100%;
          max-width: 380px;
          font-family: var(--font-noto), "Noto Sans SC", system-ui, sans-serif;
          font-size: 13px;
          line-height: 1.4;
          border: 1px solid var(--border);
          border-left: 3px solid var(--lamp);
          background: var(--card);
          color: var(--foreground);
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
          animation: noirToastIn 0.3s ease-out;
        }

        .noir-toast-icon {
          flex-shrink: 0;
          color: var(--lamp);
        }

        .noir-toast-title {
          font-weight: 600;
          letter-spacing: 0.2px;
        }

        .noir-toast-desc {
          font-size: 12px;
          opacity: 0.7;
          margin-top: 2px;
        }

        /* Success — lamp gold accent */
        .noir-toast--success {
          border-left-color: var(--lamp);
        }
        .noir-toast--success .noir-toast-icon {
          color: var(--lamp);
        }

        /* Warning — orange accent */
        .noir-toast--warning {
          border-left-color: var(--noir-orange);
        }
        .noir-toast--warning .noir-toast-icon {
          color: var(--noir-orange);
        }

        /* Error — muted red, noir-appropriate */
        .noir-toast--error {
          border-left-color: #c45c5c;
        }
        .noir-toast--error .noir-toast-icon {
          color: #c45c5c;
        }

        /* Info — subtle foreground */
        .noir-toast--info {
          border-left-color: var(--muted-foreground);
        }
        .noir-toast--info .noir-toast-icon {
          color: var(--muted-foreground);
        }

        @keyframes noirToastIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  )
}

export { Toaster }
